import React, { useState } from 'react';
import { FiSend, FiEdit2, FiTrash2, FiMoreVertical, FiCornerDownRight } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { REACTIONS } from '../../utils/constants';
import { commentsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './CommentSection.css';

// Usar solo las primeras 3 reacciones para comentarios
const COMMENT_REACTIONS = REACTIONS.slice(0, 3);

const CommentSection = ({ postId, comments, onAddComment, onEditComment, onDeleteComment, onRefresh }) => {
  const { user, isAuthenticated } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [commentReactions, setCommentReactions] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    
    await onAddComment({ text: commentText, postId });
    setCommentText('');
  };

  const handleReply = async (e, parentId) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    
    try {
      await onAddComment({ text: replyText, postId, parentId });
      setReplyText('');
      setReplyingTo(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Error al responder');
    }
  };

  const handleEdit = async (commentId) => {
    if (!editText.trim()) return;
    
    await onEditComment(commentId, { text: editText });
    setEditingId(null);
    setEditText('');
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditText(comment.text || comment.content);
    setActiveMenu(null);
  };

  const handleReaction = async (commentId, reactionType) => {
    if (!isAuthenticated) return;
    
    try {
      await commentsAPI.react(commentId, reactionType);
      // Actualizar estado local
      setCommentReactions(prev => ({
        ...prev,
        [commentId]: {
          ...prev[commentId],
          [reactionType]: !prev[commentId]?.[reactionType]
        }
      }));
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error al reaccionar:', error);
    }
  };

  const formatDate = (date) => {
    const commentDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - commentDate);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    
    if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
    if (diffMinutes < 1440) return `Hace ${Math.floor(diffMinutes / 60)}h`;
    return commentDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Organizar comentarios: padres y respuestas
  const organizeComments = (allComments) => {
    if (!allComments) return [];
    
    const parents = allComments.filter(c => !c.parent_id);
    const children = allComments.filter(c => c.parent_id);
    
    return parents.map(parent => ({
      ...parent,
      replies: children.filter(c => c.parent_id === parent.id)
    }));
  };

  const organizedComments = organizeComments(comments);

  const getUserMeta = (comment) => {
    const user = comment?.user || null;
    const name = user?.name || comment?.user_name || 'Usuario';
    const username = user?.username || comment?.user_username || null;
    const verificationLevel = user?.verification_level || comment?.user_verification_level || 'none';
    const badgeColor = user?.badge_color || comment?.user_badge_color || null;
    const badgeLabel = user?.badge_label || comment?.user_badge_label || null;

    return {
      name,
      username,
      isVerified: Boolean(verificationLevel && verificationLevel !== 'none'),
      badgeColor,
      badgeLabel
    };
  };

  const renderComment = (comment, isReply = false) => {
    const meta = getUserMeta(comment);

    return (
      <div key={comment.id} className={`comment ${isReply ? 'comment-reply' : ''}`}>
      <img 
        src={resolveMediaUrl(comment.user?.avatar || comment.user_avatar) || '/default-avatar.png'} 
        alt={meta.name}
        className="comment-avatar"
      />
      
      <div className="comment-content">
        <div className="comment-header">
          <div className="comment-author-info">
            <span className="comment-author">
              {meta.name}
              {meta.isVerified && (
                <MdVerified
                  style={{
                    marginLeft: 6,
                    verticalAlign: 'middle',
                    color: meta.badgeColor || 'currentColor'
                  }}
                  title={meta.badgeLabel || 'Cuenta verificada'}
                />
              )}
            </span>
            <span className="comment-date">{formatDate(comment.createdAt || comment.created_at)}</span>
            {comment.edited && <span className="comment-edited">(editado)</span>}
          </div>
          
          {user?.id === (comment.user?.id || comment.user_id) && (
            <div className="comment-actions">
              <button 
                className="comment-menu-btn"
                onClick={() => setActiveMenu(activeMenu === comment.id ? null : comment.id)}
              >
                <FiMoreVertical />
              </button>
              
              {activeMenu === comment.id && (
                <div className="comment-menu">
                  <button 
                    onClick={() => startEdit(comment)}
                    className="menu-item"
                  >
                    <FiEdit2 /> Editar
                  </button>
                  <button 
                    onClick={() => {
                      onDeleteComment(comment.id);
                      setActiveMenu(null);
                    }}
                    className="menu-item delete"
                  >
                    <FiTrash2 /> Eliminar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {editingId === comment.id ? (
          <div className="comment-edit-form">
            <textarea
              className="comment-edit-input"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows="3"
            />
            <div className="comment-edit-actions">
              <button 
                onClick={() => setEditingId(null)}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button 
                onClick={() => handleEdit(comment.id)}
                className="btn-save"
              >
                Guardar
              </button>
            </div>
          </div>
        ) : (
          <p className="comment-text">{comment.text || comment.content}</p>
        )}

        {/* Acciones del comentario: reacciones y responder */}
        <div className="comment-footer">
          {/* Reacciones del comentario */}
          <div className="comment-reactions">
            {COMMENT_REACTIONS.map(reaction => (
              <button
                key={reaction.id}
                className={`comment-reaction-btn ${commentReactions[comment.id]?.[reaction.id] ? 'active' : ''}`}
                onClick={() => handleReaction(comment.id, reaction.id)}
                disabled={!isAuthenticated}
                title={isAuthenticated ? reaction.label : 'Inicia sesión para reaccionar'}
              >
                <span>{reaction.emoji}</span>
                {comment[`${reaction.id}_count`] > 0 && (
                  <span className="reaction-count">{comment[`${reaction.id}_count`]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Botón responder */}
          {!isReply && isAuthenticated && (
            <button 
              className="comment-reply-btn"
              onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
            >
              <FiCornerDownRight /> Responder
            </button>
          )}
        </div>

        {/* Formulario de respuesta inline */}
        {replyingTo === comment.id && (
          <form className="reply-form" onSubmit={(e) => handleReply(e, comment.id)}>
            <img 
              src={user?.avatar || '/default-avatar.png'} 
              alt={user?.name}
              className="reply-avatar"
            />
            <div className="reply-input-wrapper">
              <input
                type="text"
                className="reply-input"
                placeholder={`Responder a ${comment.user?.name || comment.user_name}...`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                autoFocus
              />
              <button 
                type="submit" 
                className="reply-submit-btn"
                disabled={!replyText.trim()}
              >
                <FiSend />
              </button>
            </div>
          </form>
        )}

        {/* Respuestas */}
        {comment.replies?.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => renderComment(reply, true))}
          </div>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="comment-section">
      <h3 className="comment-section-title">
        Comentarios ({comments?.length || 0})
      </h3>

      {isAuthenticated ? (
        <form className="comment-form" onSubmit={handleSubmit}>
          <img 
            src={user?.avatar || '/default-avatar.png'} 
            alt={user?.name}
            className="comment-avatar"
          />
          <div className="comment-input-wrapper">
            <textarea
              className="comment-input"
              placeholder="Escribe un comentario..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows="3"
            />
            <button 
              type="submit" 
              className="comment-submit-btn"
              disabled={!commentText.trim()}
            >
              <FiSend /> Comentar
            </button>
          </div>
        </form>
      ) : (
        <div className="comment-login-inline">
          <span>Para comentar necesitas </span>
          <Link to="/login" className="inline-link">Iniciar Sesión</Link>
          <span> o </span>
          <Link to="/register" className="inline-link">Registrarte</Link>
        </div>
      )}

      <div className="comments-list">
        {organizedComments.map(comment => renderComment(comment))}
      </div>
    </div>
  );
};

export default CommentSection;
